import React, { forwardRef, useCallback, useMemo } from 'react';
import { AutocompleteValue } from '@mui/material';
import c from 'classnames';

import Input from '../../Input';
import Autocomplete from '../Autocomplete';
import { RenderInputParams } from '../BaseAutocomplete/types';
import { selectStyles } from './styles';
import { SelectProps, SelectValue } from './types';

const Select = <
  T extends { value: U },
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  U = string,
>(
  props: SelectProps<T, Multiple, DisableClearable, U>,
  ref: React.Ref<HTMLDivElement>,
) => {
  const {
    multiple,
    blurOnSelect = !multiple,
    className,
    disableCloseOnSelect = multiple,
    isOptionEqualToValue: isOptionEqualToValueProp,
    onChange: onChangeProp,
    options,
    renderValue,
    value: valueProp,
    ...rest
  } = props;

  const value = useMemo(() => {
    if (Array.isArray(valueProp)) {
      return options.filter((option) => valueProp.includes(option.value));
    }

    if (valueProp) {
      return options.find((option) => option.value === valueProp);
    }

    return valueProp || null;
  }, [options, valueProp]) as AutocompleteValue<T, Multiple, DisableClearable, false>;

  const onChange = useCallback(
    (
      _: React.SyntheticEvent<Element, Event>,
      nextValue: AutocompleteValue<T, Multiple, DisableClearable, false>,
    ) => {
      let updatedValue = null;

      if (Array.isArray(nextValue)) {
        updatedValue = nextValue.map((selectValue) => selectValue.value);
      } else if (nextValue?.value) {
        updatedValue = nextValue.value;
      }

      onChangeProp?.(updatedValue as SelectValue<U, Multiple, DisableClearable>, nextValue);
    },
    [onChangeProp],
  );

  const getRenderInputParams = useCallback(
    (params: RenderInputParams) => {
      if (!multiple && value && renderValue) {
        params.inputProps = { ...params.inputProps, value: '' };
        params.startAdornment = renderValue(value as T);
        params.placeholder = '';
      }
      return params;
    },
    [multiple, renderValue, value],
  );

  const renderInput = (_params: RenderInputParams) => {
    const params = getRenderInputParams(_params);
    return <Input {...params} readOnly />;
  };

  const isOptionEqualToValue = useCallback(
    (option: T, value: T) => {
      if (isOptionEqualToValueProp) {
        return isOptionEqualToValueProp(option, value);
      }
      return option.value === value.value;
    },
    [isOptionEqualToValueProp],
  );

  return (
    <Autocomplete
      ref={ref}
      blurOnSelect={blurOnSelect}
      className={c(selectStyles, className)}
      disableCloseOnSelect={disableCloseOnSelect}
      isOptionEqualToValue={isOptionEqualToValue}
      multiple={multiple}
      options={options}
      renderInput={renderInput}
      value={value}
      onChange={onChange}
      {...rest}
    />
  );
};

export default React.memo(forwardRef(Select)) as typeof Select;
